import { isValidElement } from "react";
import { MdInbox, MdSearchOff } from "react-icons/md";

const renderIcon = (icon, className) => {
  if (isValidElement(icon)) return icon;
  const Icon = icon;
  return <Icon aria-hidden="true" className={className} />;
};

/**
 * Empty-data placeholder. Use only when a fetch succeeded with no rows;
 * failed fetches render `ErrorState`.
 *
 * @param {object} props
 * @param {React.ElementType|React.ReactElement} [props.icon] Icon component (e.g. `MdGroups`) or element.
 *   Defaults to MdInbox (first-run) / MdSearchOff (filtered). Ignored by `inline`.
 * @param {React.ReactNode} [props.title] Heading. `filtered` defaults to "No results match your filters".
 * @param {"h1"|"h2"|"h3"|"h4"} [props.titleAs="h3"] Heading element; use "h1" on standalone screens with no other h1.
 * @param {React.ReactNode} [props.description] Supporting copy.
 * @param {React.ReactNode} [props.action] Action slot, typically a `Button`.
 * @param {"first-run"|"filtered"|"inline"} [props.variant="first-run"]
 *   `first-run`: nothing created yet. `filtered`: search/filters excluded everything.
 *   `inline`: one muted line inside a card, list or table.
 * @param {string} [props.className]
 */
const EmptyState = ({
  icon,
  title,
  titleAs: TitleTag = "h3",
  description,
  action,
  variant = "first-run",
  className = "",
}) => {
  if (variant === "inline") {
    return (
      <p className={`py-6 text-center text-sm text-fg-muted ${className}`.trim()}>
        {title || description}
      </p>
    );
  }

  const filtered = variant === "filtered";
  const heading = title || (filtered ? "No results match your filters" : null);

  return (
    <div
      className={`flex flex-col items-center justify-center px-4 text-center ${
        filtered ? "py-10" : "py-12"
      } ${className}`.trim()}
    >
      <div
        className={`mb-4 flex items-center justify-center rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 ${
          filtered ? "h-10 w-10" : "h-12 w-12"
        }`}
      >
        {renderIcon(icon || (filtered ? MdSearchOff : MdInbox), filtered ? "h-5 w-5" : "h-6 w-6")}
      </div>
      {heading && <TitleTag className="text-heading">{heading}</TitleTag>}
      {description && (
        <p className="mt-1 max-w-md text-sm text-fg-muted">{description}</p>
      )}
      {action && <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
};

export default EmptyState;
